import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2 } from "lucide-react";
import { useSalesOrders } from "@/hooks/useSalesOrders";
import { SalesOrderListItem, SalesOrderStatus } from "@/types/salesDocuments";
import { format } from "date-fns";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

interface SalesOrderPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSalesOrder: (salesOrder: SalesOrderListItem) => void;
  onlyPartiallyDelivered?: boolean;
  description?: string;
}

export const SalesOrderPickerModal = ({
  isOpen,
  onClose,
  onSelectSalesOrder,
  onlyPartiallyDelivered = false,
  description,
}: SalesOrderPickerModalProps) => {
  const { format: formatDecimal } = useDecimalPrecision();
  const [searchTerm, setSearchTerm] = useState("");

  const { data: salesOrdersData, isLoading, error } = useSalesOrders({
    search: searchTerm,
  });

  // Filter sales orders based on delivery status
  const filteredSalesOrders = salesOrdersData?.data?.filter((salesOrder) => {
    // Exclude fully fulfilled or cancelled orders
    if (
      salesOrder.status === SalesOrderStatus.DELIVERED ||
      salesOrder.status === SalesOrderStatus.FULFILLED ||
      salesOrder.status === SalesOrderStatus.CANCELLED
    ) {
      return false;
    }

    // If only showing partially delivered, filter accordingly
    if (onlyPartiallyDelivered) {
      return salesOrder.deliveryStatus === "Partial" || salesOrder.deliveryStatus === "Not Started";
    }

    return true;
  }) || [];

  const getStatusColor = (status: SalesOrderStatus) => {
    const baseStyle = "rounded-full px-3 py-1 text-xs font-medium";
    switch (status) {
      case SalesOrderStatus.DRAFT:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
      case SalesOrderStatus.CONFIRMED:
        return `${baseStyle} bg-blue-100 text-blue-800`;
      case SalesOrderStatus.PROCESSING:
        return `${baseStyle} bg-yellow-100 text-yellow-800`;
      case SalesOrderStatus.PARTIALLY_DELIVERED:
        return `${baseStyle} bg-orange-100 text-orange-800`;
      case SalesOrderStatus.DELIVERED:
        return `${baseStyle} bg-green-100 text-green-800`;
      case SalesOrderStatus.PARTIALLY_FULFILLED:
        return `${baseStyle} bg-orange-100 text-orange-800`;
      case SalesOrderStatus.FULFILLED:
        return `${baseStyle} bg-green-100 text-green-800`;
      case SalesOrderStatus.CANCELLED:
        return `${baseStyle} bg-red-100 text-red-800`;
      default:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  const getDeliveryStatusColor = (status: string) => {
    const baseStyle = "rounded-full px-2 py-0.5 text-xs font-medium";
    switch (status) {
      case "Not Started":
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
      case "Partial":
        return `${baseStyle} bg-orange-100 text-orange-800`;
      case "Complete":
        return `${baseStyle} bg-green-100 text-green-800`;
      default:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  const handleSelect = (salesOrder: SalesOrderListItem) => {
    onSelectSalesOrder(salesOrder);
    onClose();
    setSearchTerm("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Select Sales Order</DialogTitle>
          <DialogDescription>
            {description ?? "Search and select a sales order to create a delivery order from. Only showing orders with pending deliveries."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search by order number, buyer name, PO number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
              aria-label="Search sales orders"
            />
          </div>
          <div className="relative border rounded-md">
            <Table>
              <TableHeader className="bg-white dark:bg-gray-950 sticky top-0">
                <TableRow>
                  <TableHead className="w-[150px]">Order No.</TableHead>
                  <TableHead className="w-[180px]">Buyer Name</TableHead>
                  <TableHead className="w-[120px]">Order Date</TableHead>
                  <TableHead className="w-[120px]">Expected Delivery</TableHead>
                  <TableHead className="w-[120px]">Amount (RM)</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[100px]">Delivery</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
            </Table>
            <div className="max-h-[40vh] overflow-auto">
              <Table>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        <p className="mt-2 text-sm text-muted-foreground">Loading sales orders...</p>
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-red-500 py-8">
                        Error loading sales orders
                      </TableCell>
                    </TableRow>
                  ) : filteredSalesOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <p className="text-muted-foreground">
                          {searchTerm
                            ? "No sales orders found matching your search"
                            : "No sales orders available for delivery"}
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSalesOrders.map((salesOrder) => (
                      <TableRow key={salesOrder.id} className="hover:bg-muted">
                        <TableCell className="w-[150px] font-medium">
                          {salesOrder.codeNumber}
                          {salesOrder.quotationNumber && (
                            <div className="text-xs text-gray-500">
                              From: {salesOrder.quotationNumber}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="w-[180px]">
                          {salesOrder.buyerName || "N/A"}
                          <div className="text-xs text-gray-500">{salesOrder.buyerCode}</div>
                        </TableCell>
                        <TableCell className="w-[120px]">
                          {format(new Date(salesOrder.orderDate), "dd/MM/yyyy")}
                        </TableCell>
                        <TableCell className="w-[120px]">
                          {salesOrder.expectedDeliveryDate
                            ? format(new Date(salesOrder.expectedDeliveryDate), "dd/MM/yyyy")
                            : "N/A"}
                        </TableCell>
                        <TableCell className="w-[120px] text-right">
                          {formatDecimal(salesOrder.totalPayableAmount).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        </TableCell>
                        <TableCell className="w-[100px]">
                          <Badge className={getStatusColor(salesOrder.status)}>
                            {salesOrder.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="w-[100px]">
                          <Badge className={getDeliveryStatusColor(salesOrder.deliveryStatus)}>
                            {salesOrder.deliveryStatus}
                          </Badge>
                        </TableCell>
                        <TableCell className="w-[100px]">
                          <Button
                            size="sm"
                            onClick={() => handleSelect(salesOrder)}
                            aria-label={`Select sales order ${salesOrder.codeNumber}`}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SalesOrderPickerModal;
